// components/hr/CredentialRequirementsPanel.tsx
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Dropdown,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  Plus as AddOutlined,
  DotsThreeVertical as MoreVertOutlined,
  PencilSimple as EditOutlined,
  Trash as DeleteOutlined,
  ShieldCheck as ShieldCheckOutlined,
  Warning as WarningOutlined,
  CheckCircle as CheckCircleOutlined,
  ClipboardText as ClipboardOutlined,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { apiClient } from '../../lib/api';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import { CREDENTIAL_TYPE_LABELS, CREDENTIAL_TYPE_OPTIONS, type CredentialType } from './TrainingEditModal';
import type { TeamMemberListItem } from '../../types/hr';

/**
 * A named set of credentials a kind of work requires.
 *
 * There is no job_type on a job, so the anchor is a set a dispatcher picks by
 * name. match_job_tags is a hint a future dispatch screen can use to suggest
 * the right one from a job's tags.
 */
interface RequirementSet {
  id: string;
  name: string;
  description: string;
  credential_types: CredentialType[];
  credential_type_labels: string[];
  match_job_tags: string[];
  is_active: boolean;
  override_count: number;
  created_at: string;
}

/** Cell statuses from the compliance service, shared with the compliance wall. */
type CellStatus =
  | 'expired'
  | 'overdue'
  | 'expiring_soon'
  | 'in_progress'
  | 'not_started'
  | 'complete';

type Severity = 'satisfied' | 'warning' | 'blocking';

interface VerdictCredential {
  credential_type: CredentialType;
  credential_type_display: string;
  status: CellStatus;
  severity: Severity;
  training_item_title: string | null;
  has_training_item: boolean;
  expires_at: string | null;
  days_until_expiry: number | null;
}

interface ActiveOverride {
  id: string;
  reason: string;
  job_reference: string;
  overridden_by_name: string | null;
  created_at: string;
  expires_at: string | null;
}

interface Verdict {
  team_member_id: string;
  team_member_name: string;
  requirement_set_name: string;
  status: 'clear' | 'warning' | 'not_qualified';
  is_clear: boolean;
  requires_override: boolean;
  blocking_count: number;
  warning_count: number;
  credentials: VerdictCredential[];
  missing_credentials: CredentialType[];
  active_overrides: ActiveOverride[];
}

const SEVERITY_STYLES: Record<Severity, { color: 'success' | 'warning' | 'danger'; label: string }> = {
  satisfied: { color: 'success', label: 'Current' },
  warning: { color: 'warning', label: 'Expiring soon' },
  blocking: { color: 'danger', label: 'Not held' },
};

const STATUS_LABELS: Record<CellStatus, string> = {
  expired: 'Expired',
  overdue: 'Overdue',
  expiring_soon: 'Expiring soon',
  in_progress: 'In progress',
  not_started: 'Never completed',
  complete: 'Current',
};

/**
 * apiClient attaches the parsed error body as `responseData` — not the axios
 * `response.data` shape the shared helper in TrainingEditModal reads — so field
 * errors like a too-short override reason would otherwise never reach the user.
 */
const errorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { responseData?: unknown })?.responseData;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    for (const value of Object.values(data as Record<string, unknown>)) {
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }
  return fallback;
};

const useRequirementSets = () =>
  useQuery({
    queryKey: ['credential-requirements'],
    queryFn: async () => {
      const response = await apiClient.get<{ results: RequirementSet[] } | RequirementSet[]>(
        '/api/training/credential-requirements/?page_size=200'
      );
      return Array.isArray(response.data) ? response.data : response.data.results;
    },
  });

const useTeamMembers = () =>
  useQuery({
    queryKey: ['team-member-contacts'],
    queryFn: async () => {
      const response = await apiClient.get<{ results: TeamMemberListItem[] } | TeamMemberListItem[]>(
        '/api/hr/team-members/?page_size=1000'
      );
      return Array.isArray(response.data) ? response.data : response.data.results;
    },
  });

// ---------------------------------------------------------------------------
// Create / edit a requirement set
// ---------------------------------------------------------------------------

interface RequirementSetForm {
  name: string;
  description: string;
  credential_types: CredentialType[];
  job_tags: string;
  is_active: boolean;
}

const emptyForm: RequirementSetForm = {
  name: '',
  description: '',
  credential_types: [],
  job_tags: '',
  is_active: true,
};

const toForm = (set: RequirementSet): RequirementSetForm => ({
  name: set.name,
  description: set.description || '',
  credential_types: set.credential_types || [],
  job_tags: (set.match_job_tags || []).join(', '),
  is_active: set.is_active,
});

interface RequirementSetModalProps {
  open: boolean;
  editing: RequirementSet | null;
  onClose: () => void;
}

const RequirementSetModal: React.FC<RequirementSetModalProps> = ({ open, editing, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RequirementSetForm>(emptyForm);
  const [error, setError] = useState('');

  // Re-seed whenever the modal is opened for a different row.
  React.useEffect(() => {
    if (open) {
      setForm(editing ? toForm(editing) : emptyForm);
      setError('');
    }
  }, [open, editing]);

  const save = useMutation({
    mutationFn: async (payload: RequirementSetForm) => {
      const body = {
        name: payload.name.trim(),
        description: payload.description.trim(),
        credential_types: payload.credential_types,
        match_job_tags: payload.job_tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        is_active: payload.is_active,
      };
      const response = editing
        ? await apiClient.patch<RequirementSet>(
            `/api/training/credential-requirements/${editing.id}/`,
            body
          )
        : await apiClient.post<RequirementSet>('/api/training/credential-requirements/', body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-requirements'] });
      toast.success(editing ? 'Requirement set updated' : 'Requirement set created');
      onClose();
    },
    onError: (err) => setError(errorMessage(err, 'Could not save this requirement set')),
  });

  const handleSave = () => {
    if (!form.name.trim()) {
      setError('Give this set a name a dispatcher will recognise.');
      return;
    }
    if (form.credential_types.length === 0) {
      setError('Pick at least one credential — an empty set clears everybody.');
      return;
    }
    setError('');
    save.mutate(form);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: { xs: 'auto', sm: 520 }, maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <Typography level="h4">
          {editing ? 'Edit Requirement Set' : 'New Requirement Set'}
        </Typography>
        <Typography level="body-sm" color="neutral">
          What a kind of work requires before somebody can be sent to do it.
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          {error && <Alert color="danger">{error}</Alert>}

          <FormControl required>
            <FormLabel>Name</FormLabel>
            <Input
              placeholder="e.g. Steep-slope tear-off"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              minRows={2}
              placeholder="What this covers, so the next dispatcher picks the right one"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </FormControl>

          <FormControl required>
            <FormLabel>Required credentials</FormLabel>
            <Select
              multiple
              value={form.credential_types}
              onChange={(_, value) =>
                setForm({ ...form, credential_types: (value as CredentialType[]) || [] })
              }
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selected.map((option) => (
                    <Chip key={option.value as string} size="sm" variant="soft">
                      {CREDENTIAL_TYPE_LABELS[option.value as CredentialType]}
                    </Chip>
                  ))}
                </Box>
              )}
            >
              {CREDENTIAL_TYPE_OPTIONS.map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
            <FormHelperText>
              Any current course granting the credential satisfies it.
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Job tags (optional)</FormLabel>
            <Input
              placeholder="tear-off, steep-slope"
              value={form.job_tags}
              onChange={(e) => setForm({ ...form, job_tags: e.target.value })}
            />
            <FormHelperText>
              Comma separated. A hint so a job carrying these tags can suggest this set.
            </FormHelperText>
          </FormControl>

          <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <Box>
              <FormLabel>Active</FormLabel>
              <FormHelperText sx={{ mt: 0 }}>Offered when checking a team member.</FormHelperText>
            </Box>
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button variant="plain" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={save.isPending}>
              {editing ? 'Save Changes' : 'Create Set'}
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Check a team member, and record an override
// ---------------------------------------------------------------------------

interface CredentialCheckModalProps {
  open: boolean;
  requirementSet: RequirementSet | null;
  onClose: () => void;
}

const CredentialCheckModal: React.FC<CredentialCheckModalProps> = ({
  open,
  requirementSet,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [teamMemberId, setTeamMemberId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [jobReference, setJobReference] = useState('');
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (open) {
      setTeamMemberId('');
      setReason('');
      setJobReference('');
      setError('');
    }
  }, [open, requirementSet]);

  const { data: members = [] } = useTeamMembers();
  const activeMembers = useMemo(
    () => members.filter((m) => m.status !== 'terminated' && m.status !== 'inactive'),
    [members]
  );

  const { data: verdict, isFetching } = useQuery({
    queryKey: ['credential-check', requirementSet?.id, teamMemberId],
    enabled: Boolean(open && requirementSet && teamMemberId),
    queryFn: async () => {
      const response = await apiClient.get<Verdict>(
        `/api/training/credential-requirements/${requirementSet!.id}/check/?team_member=${teamMemberId}`
      );
      return response.data;
    },
  });

  const override = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ id: string }>(
        `/api/training/credential-requirements/${requirementSet!.id}/override/`,
        { team_member: teamMemberId, reason: reason.trim(), job_reference: jobReference.trim() }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-check'] });
      queryClient.invalidateQueries({ queryKey: ['credential-requirements'] });
      setReason('');
      toast.success('Override recorded');
    },
    onError: (err) => setError(errorMessage(err, 'Could not record the override')),
  });

  const handleOverride = () => {
    if (reason.trim().length < 10) {
      setError('Write a sentence. "OK" is not a record an inspector can read.');
      return;
    }
    setError('');
    override.mutate();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: { xs: 'auto', sm: 560 }, maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <Typography level="h4">Check Credentials</Typography>
        <Typography level="body-sm" color="neutral">
          {requirementSet?.name}
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl>
            <FormLabel>Team member</FormLabel>
            <Select
              placeholder="Who is being assigned?"
              value={teamMemberId || null}
              onChange={(_, value) => setTeamMemberId((value as string) || '')}
            >
              {activeMembers.map((member) => (
                <Option key={member.id} value={member.id}>
                  {member.full_name}
                  {member.job_title ? ` — ${member.job_title}` : ''}
                </Option>
              ))}
            </Select>
          </FormControl>

          {isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          )}

          {verdict && !isFetching && (
            <>
              <Alert
                color={verdict.requires_override ? 'danger' : verdict.warning_count ? 'warning' : 'success'}
                startDecorator={
                  verdict.requires_override ? <WarningOutlined /> : <CheckCircleOutlined />
                }
              >
                <Box>
                  <Typography level="title-sm">
                    {verdict.requires_override
                      ? `${verdict.team_member_name} is short ${verdict.blocking_count} credential${
                          verdict.blocking_count === 1 ? '' : 's'
                        }`
                      : verdict.warning_count
                        ? `${verdict.team_member_name} is clear, with ${verdict.warning_count} credential expiring soon`
                        : `${verdict.team_member_name} is clear for this work`}
                  </Typography>
                  {verdict.requires_override && (
                    // Soft block, on purpose: a hard block gets routed around by
                    // text message and the record disappears with it.
                    <Typography level="body-xs">
                      This does not stop the assignment. Record why, and it is on file.
                    </Typography>
                  )}
                </Box>
              </Alert>

              <Stack spacing={1}>
                {verdict.credentials.map((credential) => (
                  <Box
                    key={credential.credential_type}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography level="body-sm">{credential.credential_type_display}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {credential.has_training_item
                          ? credential.training_item_title
                          : 'No active course grants this credential'}
                      </Typography>
                    </Box>
                    <Chip size="sm" variant="soft" color={SEVERITY_STYLES[credential.severity].color}>
                      {STATUS_LABELS[credential.status]}
                      {credential.days_until_expiry !== null && credential.severity === 'warning'
                        ? ` (${credential.days_until_expiry}d)`
                        : ''}
                    </Chip>
                  </Box>
                ))}
              </Stack>

              {verdict.active_overrides.length > 0 && (
                <Alert color="neutral" variant="soft" startDecorator={<ClipboardOutlined />}>
                  <Box>
                    <Typography level="title-sm">Overrides on file</Typography>
                    {verdict.active_overrides.map((entry) => (
                      <Typography key={entry.id} level="body-xs">
                        {new Date(entry.created_at).toLocaleDateString()} —{' '}
                        {entry.overridden_by_name || 'Unknown'}: {entry.reason}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              <Divider />

              <Typography level="title-sm">Send them anyway</Typography>
              {error && <Alert color="danger">{error}</Alert>}
              <FormControl>
                <FormLabel>Reason</FormLabel>
                <Textarea
                  minRows={2}
                  placeholder="e.g. Renewal booked Friday; foreman supervising until then."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <FormHelperText>
                  Recorded with your name and the time. This is what an insurer asks for.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Job reference (optional)</FormLabel>
                <Input
                  placeholder="Job number, address or crew"
                  value={jobReference}
                  onChange={(e) => setJobReference(e.target.value)}
                />
              </FormControl>
              <Button
                color="warning"
                variant="soft"
                onClick={handleOverride}
                loading={override.isPending}
              >
                Record Override
              </Button>
            </>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * HR defines which credentials a kind of work needs; the check beside each set
 * is the same verdict a dispatch screen would call before assigning somebody.
 */
export const CredentialRequirementsPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: sets = [], isLoading } = useRequirementSets();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RequirementSet | null>(null);
  const [checking, setChecking] = useState<RequirementSet | null>(null);

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/training/credential-requirements/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential-requirements'] });
      toast.success('Requirement set deleted');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not delete this requirement set')),
  });

  const handleDelete = (set: RequirementSet) => {
    const warning = set.override_count
      ? `\n\n${set.override_count} override record(s) stay on file.`
      : '';
    if (window.confirm(`Delete "${set.name}"?${warning}`)) {
      remove.mutate(set.id);
    }
  };

  const columns: ResponsiveColumn<RequirementSet>[] = [
    {
      key: 'name',
      sortValue: (row) => row.name,
      header: 'Requirement Set',
      mobileVariant: 'header',
      render: (set) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
            {set.name}
          </Typography>
          {set.description && (
            <Typography level="body-xs" color="neutral">
              {set.description.substring(0, 80)}
              {set.description.length > 80 ? '...' : ''}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'credentials',
      sortValue: (row) => row.credential_type_labels?.length ?? 0,
      header: 'Required Credentials',
      render: (set) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {set.credential_type_labels.map((label) => (
            <Chip key={label} size="sm" variant="soft" color="primary">
              {label}
            </Chip>
          ))}
        </Box>
      ),
    },
    {
      key: 'job_tags',
      sortValue: (row) => row.match_job_tags?.length ?? 0,
      header: 'Job Tags',
      render: (set) =>
        set.match_job_tags.length ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {set.match_job_tags.map((tag) => (
              <Chip key={tag} size="sm" variant="outlined">
                {tag}
              </Chip>
            ))}
          </Box>
        ) : (
          <Typography level="body-xs" color="neutral">
            —
          </Typography>
        ),
    },
    {
      key: 'overrides',
      sortValue: (row) => row.override_count,
      header: 'Overrides',
      render: (set) => (
        <Typography level="body-sm" color={set.override_count ? 'warning' : 'neutral'}>
          {set.override_count}
        </Typography>
      ),
    },
    {
      key: 'status',
      sortValue: (row) => row.is_active,
      header: 'Status',
      render: (set) => (
        <Chip size="sm" variant="soft" color={set.is_active ? 'success' : 'neutral'}>
          {set.is_active ? 'Active' : 'Inactive'}
        </Chip>
      ),
    },
  ];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Typography level="title-lg">Credential Requirements</Typography>
          <Typography level="body-sm" color="neutral">
            Which credentials a kind of work needs. Checking somebody warns — it never blocks the
            assignment, it records the decision.
          </Typography>
        </Box>
        <Button
          startDecorator={<AddOutlined />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          New Requirement Set
        </Button>
      </Stack>

      {isLoading ? (
        <Card>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Card>
      ) : (
        <Card>
          <ResponsiveTable<RequirementSet>
            rows={sets}
            getRowKey={(set) => set.id}
            columns={columns}
            rowActions={(set) => (
              <Dropdown>
                <MenuButton
                  slots={{ root: IconButton }}
                  slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                >
                  <MoreVertOutlined />
                </MenuButton>
                <Menu placement="bottom-end">
                  <MenuItem onClick={() => setChecking(set)}>
                    <ShieldCheckOutlined style={{ marginRight: 8 }} />
                    Check a team member
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setEditing(set);
                      setModalOpen(true);
                    }}
                  >
                    <EditOutlined style={{ marginRight: 8 }} />
                    Edit
                  </MenuItem>
                  <Divider />
                  <MenuItem color="danger" onClick={() => handleDelete(set)}>
                    <DeleteOutlined style={{ marginRight: 8 }} />
                    Delete
                  </MenuItem>
                </Menu>
              </Dropdown>
            )}
            emptyState={
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ShieldCheckOutlined size={48} color="var(--joy-palette-neutral-400)" />
                <Typography level="body-lg" sx={{ mt: 1 }}>
                  No requirement sets yet
                </Typography>
                <Typography level="body-sm" color="neutral">
                  Define what steep-slope work, driving or tear-offs require, then check a team
                  member before assigning them.
                </Typography>
              </Box>
            }
          />
        </Card>
      )}

      <RequirementSetModal
        open={modalOpen}
        editing={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
      <CredentialCheckModal
        open={Boolean(checking)}
        requirementSet={checking}
        onClose={() => setChecking(null)}
      />
    </Box>
  );
};
